import { useMutation, useQuery } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../commonApis/commonApis";

const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

interface NotificationVariable {
  key: string;
  placeholder: string;
  description?: string;
}

export const useFetchOrderStatus = () => {
  return useQuery<string[]>({
    queryKey: ["order-statuses"],
    queryFn: async () => {
      const res: ApiResponse = await fetch3(
        `${url}/api/shipment/notification/order-statuses`,
        "GET"
      );

      if (!res?.success) {
        throw new Error(res?.message || "Failed to fetch Order Status");
      }

      return res.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useFetchNotificationVariables = (status?: string) => {
  return useQuery<NotificationVariable[]>({
    queryKey: ["notification-variables", status],
    enabled: !!status,
    queryFn: async () => {
      const res: ApiResponse = await fetch3(
        `${url}/api/shipment/notification/order-statuses/${status}/variables`,
        "GET"
      );

      if (!res?.success) {
        throw new Error(res?.message || "Failed to fetch variables");
      }

      return res.data;
    },
  });
};

export const usePostNotificationTemplate = () => {
  return useMutation<ApiResponse, Error, any>({
    mutationFn: async (payload) => {
      const res: ApiResponse = await fetch4(
        `${url}/api/shipment/notification/templates`,
        payload
      );

      if (!res?.success) {
        throw new Error(res?.message || "Failed to create template");
      }

      return res;
    },

    onSuccess: (res) => {
      console.log("Notification Template created:", res);
    },

    onError: (error) => {
      console.error("Notification Template failed:", error.message);
    },
  });
};

export interface ShipmentTemplate {
  id: string;
  orderStatus: string;
  channels: string[];
  languages: string[];
  messageEnglish?: string;
  messageArabic?: string;
  createdBy: string;
  createdAt: string;
}


export const useFetchShipmentNotificationTemplate = () => {
  return useQuery<ShipmentTemplate[]>({
    queryKey: ["shipmentnotification"],
    queryFn: async () => {
      const res: ApiResponse = await fetch3(
        `${url}/api/shipment/notification/get/templates`,
        "GET"
      );

      if (!res?.success) {
        throw new Error(res?.message || "Failed to Fetch Shipment Notification Templates");
      }

      return res.data; // ShipmentTemplate[]
    },
    staleTime: 10 * 60 * 1000,
  });
};


export const useFetchShipmentNotificationTemplateById = (id?: string) => {
  return useQuery<ShipmentTemplate>({
    queryKey: ["shipmentnotificationbyid", id],
    enabled: !!id, // 🔥 prevents API call when id is undefined
    queryFn: async () => {
      const res: ApiResponse = await fetch3(
        `${url}/api/shipment/notification/get-template-byid/${id}`,
        "GET"
      );

      if (!res?.success) {
        throw new Error(res?.message || "Failed to Fetch Shipment Notification Template");
      }

      return res.data;
    },
  });
};




// Update Notification Template
export const useUpdateNotificationTemplate = (id:string) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/shipment/notification/templates/${id}`,
        "PUT",
        payload
      );
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
  });
};


// Update Status Api of Notification template

type ShipmentStatusPayload = {
  id: string;
  status: "ACTIVE" | "INACTIVE";
};

export const useUpdateShipmentTemplateStatus = () => {
  return useMutation({
    mutationFn: async ({ id, status }: ShipmentStatusPayload) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/shipment/notification/templates/${id}/status`,
        "PATCH",
        { status }
      );

      if (!res.success) {
        throw new Error(res.message || "Something went wrong");
      }

      return res.data;
    },
  });
};




